import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import api, { ApiError } from '../../utils/api';
import { SERVICE_CATALOG, formatDuration } from '../../constants/services';
import DatePills from './DatePills';
import TimeSlots from './TimeSlots';
import { toast } from '../../utils/swal';

import './BookingForm.css';

const INITIAL_FORM = {
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  email2: '',
  note: '',
  consent: false,
  website: '',
};

const STEP_COUNT = 3;

export default function BookingForm() {
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [animating, setAnimating] = useState(false);
  const [direction, setDirection] = useState('forward'); // 'forward' | 'back'

  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const [selectedService, setSelectedService] = useState('');

  const [dates, setDates] = useState([]);
  const [datesLoading, setDatesLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState('');

  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedTime, setSelectedTime] = useState('');

  const formRef = useRef(null);
  const slotsAbortRef = useRef(null);

  // load weekday dates on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setDatesLoading(true);
      try {
        const res = await api.get('/api/dates');
        if (cancelled) return;
        const list = (res && res.dates) || [];
        setDates(list);
        if (list.length) setSelectedDate(list[0].iso);
      } catch (err) {
        if (!cancelled) { console.error('[booking] dates load failed:', err); setDates([]); }
      } finally {
        if (!cancelled) setDatesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // load availability when date or service changes
  useEffect(() => {
    if (!selectedDate || !selectedService) return;

    if (slotsAbortRef.current) slotsAbortRef.current.abort();
    const controller = new AbortController();
    slotsAbortRef.current = controller;

    setSlotsLoading(true);
    setSlots([]);

    (async () => {
      try {
        const res = await api.get('/api/availability', {
          query: { date: selectedDate, service: selectedService },
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        const newSlots = (res && res.slots) || [];
        setSlots(newSlots);
        setSelectedTime((prev) => {
          if (!prev) return prev;
          return newSlots.find((s) => s.time === prev && s.available) ? prev : '';
        });
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('[booking] availability load failed:', err);
        setSlots([]);
      } finally {
        if (!controller.signal.aborted) setSlotsLoading(false);
      }
    })();

    return () => controller.abort();
  }, [selectedDate, selectedService]);

  // ── step transitions ────────────────────────────
  const goTo = (next, dir = 'forward') => {
    if (animating) return;
    setDirection(dir);
    setAnimating(true);
    setTimeout(() => {
      setStep(next);
      setAnimating(false);
    }, 280);
  };

  const goNext = () => goTo(step + 1, 'forward');
  const goBack = () => {
    setErrors({});
    goTo(step - 1, 'back');
  };

  // ── handlers ────────────────────────────────────
  const clearError = (key) =>
    setErrors((e) => { if (!e[key]) return e; const n = { ...e }; delete n[key]; return n; });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
    clearError(name);
  };

  const handleServiceSelect = (name) => {
    setSelectedService(name);
    clearError('service');
  };

  const handleDateChange = (iso) => { setSelectedDate(iso); clearError('date'); };
  const handleTimeChange = (time) => { setSelectedTime(time); clearError('time'); };

  // ── validation per step ─────────────────────────
  const validateStep2 = () => {
    const errs = {};
    if (!selectedDate) errs.date = 'Pick a day.';
    if (!selectedTime) errs.time = 'Pick a time.';
    return errs;
  };

  const validateStep3 = () => {
    const errs = {};
    if (!form.firstName.trim()) errs.firstName = 'Please tell us your first name.';
    if (!form.lastName.trim()) errs.lastName = 'And your last name?';
    const digits = (form.phone || '').replace(/\D/g, '');
    if (digits.length !== 10 && !(digits.length === 11 && digits.startsWith('1')))
      errs.phone = 'Enter a 10-digit phone number.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email || ''))
      errs.email = 'That email looks off.';
    if (form.email !== form.email2) errs.email2 = 'Emails don\u2019t match.';
    if (!form.consent) errs.consent = 'Please agree to receive appointment emails.';
    return errs;
  };

  const handleStep2Next = () => {
    const errs = validateStep2();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    goNext();
  };

  const scrollToFirstError = (errs) => {
    if (!formRef.current) return;
    const order = ['firstName', 'lastName', 'phone', 'email', 'email2', 'consent'];
    const key = order.find((k) => errs[k]);
    if (!key) return;
    const el = formRef.current.querySelector(`[name="${key}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => el.focus({ preventScroll: true }), 350);
    }
  };

  // ── submit ──────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const errs = validateStep3();
    if (Object.keys(errs).length) { setErrors(errs); scrollToFirstError(errs); return; }

    setSubmitting(true);
    try {
      const payload = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        email2: form.email2.trim(),
        service: selectedService,
        date: selectedDate,
        time: selectedTime,
        note: form.note.trim(),
        consent: !!form.consent,
        website: form.website,
      };
      const res = await api.post('/api/book', payload);
      const appt = res && res.appointment;
      if (appt && appt.appointmentId) {
        toast(`You're on the list, ${appt.firstName}.`, 'success');
        navigate(`/confirmation/${encodeURIComponent(appt.appointmentId)}`);
      } else {
        toast('Booking received.', 'success');
      }
      setForm(INITIAL_FORM);
      setSelectedService('');
      setSelectedTime('');
      setStep(1);
    } catch (err) {
      if (err instanceof ApiError && err.errors) {
        setErrors(err.errors);
        scrollToFirstError(err.errors);
      } else {
        setErrors((e) => ({ ...e, _form: (err && err.message) || 'Could not save your booking. Please try again.' }));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const progress = (step / STEP_COUNT) * 100;

  return (
    <div className="booking">
      <div className="booking__inner">

        {/* ── side header ── */}
        <aside className="booking__head">
          <div className="booking__kicker mono">
            <span className="booking__num">01</span>
            <span>Reserve your visit</span>
          </div>
          <h2 className="booking__heading display">
            Book a <em>chair</em>.
          </h2>
          <p className="booking__intro">
            Pick a service, a day, and a time. We'll confirm by email and
            keep your spot waiting.
          </p>
          <div className="booking__ornament" aria-hidden="true">※</div>
        </aside>

        {/* ── wizard card ── */}
        <div className="booking__card">

          {/* honeypot — outside form so it's never submitted accidentally */}
          <div className="booking__honey" aria-hidden="true">
            <label>
              Website
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={handleChange}
              />
            </label>
          </div>

          {/* progress bar */}
          <div className="booking__progress">
            <div className="booking__progress-track">
              <div
                className="booking__progress-fill"
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className="booking__step-counter mono">
              {String(step).padStart(2, '0')} / {String(STEP_COUNT).padStart(2, '0')}
            </span>
          </div>

          {/* steps */}
          <div className="booking__steps">

            {/* ── Step 1: Service ── */}
            <div className={`booking__step ${step === 1 ? 'booking__step--active' : ''} ${animating && step === 1 ? (direction === 'forward' ? 'booking__step--exit-back' : 'booking__step--exit-forward') : ''}`}>
              <div className="booking__step-head">
                <h3 className="booking__step-title display">Choose a <em>service</em></h3>
                <p className="booking__step-sub">What brings you in?</p>
              </div>

              <div className="booking__services">
                {SERVICE_CATALOG.map((s) => (
                  <button
                    key={s.name}
                    type="button"
                    className={`booking__service-card ${selectedService === s.name ? 'booking__service-card--selected' : ''}`}
                    onClick={() => handleServiceSelect(s.name)}
                  >
                    <div className="booking__service-info">
                      <span className="booking__service-name">{s.name}</span>
                      <span className="booking__service-dur mono">{formatDuration(s.durationMinutes)}</span>
                    </div>
                    <span className="booking__service-check" aria-hidden="true">✓</span>
                  </button>
                ))}
              </div>

              {errors.service && <span className="booking__err">{errors.service}</span>}

              <div className="booking__step-actions">
                <button
                  type="button"
                  className="booking__btn-next"
                  disabled={!selectedService}
                  onClick={goNext}
                >
                  <span>Continue</span>
                  <span className="booking__arrow" aria-hidden="true">→</span>
                </button>
              </div>
            </div>

            {/* ── Step 2: Date + Time ── */}
            <div className={`booking__step ${step === 2 ? 'booking__step--active' : ''} ${animating && step === 2 ? (direction === 'forward' ? 'booking__step--exit-back' : 'booking__step--exit-forward') : ''}`}>
              <div className="booking__step-head">
                <h3 className="booking__step-title display">Pick a <em>date</em></h3>
                <p className="booking__step-sub">
                  {selectedService} — {formatDuration(SERVICE_CATALOG.find(s => s.name === selectedService)?.durationMinutes || 0)}
                </p>
              </div>

              <div className="booking__datetime">
                <div className="booking__field">
                  <label className="booking__label mono">
                    Date <span className="booking__req">*</span>
                  </label>
                  <DatePills
                    dates={dates}
                    value={selectedDate}
                    onChange={handleDateChange}
                    loading={datesLoading}
                  />
                  {errors.date && <span className="booking__err">{errors.date}</span>}
                </div>

                <div className="booking__field">
                  <label className="booking__label mono">
                    Time <span className="booking__req">*</span>
                  </label>
                  <TimeSlots
                    slots={slots}
                    value={selectedTime}
                    onChange={handleTimeChange}
                    loading={slotsLoading}
                  />
                  {errors.time && <span className="booking__err">{errors.time}</span>}
                </div>
              </div>

              <div className="booking__step-actions booking__step-actions--split">
                <button type="button" className="booking__btn-back" onClick={goBack}>
                  ← Back
                </button>
                <button
                  type="button"
                  className="booking__btn-next"
                  disabled={!selectedDate || !selectedTime}
                  onClick={handleStep2Next}
                >
                  <span>Continue</span>
                  <span className="booking__arrow" aria-hidden="true">→</span>
                </button>
              </div>
            </div>

            {/* ── Step 3: Details ── */}
            <div className={`booking__step ${step === 3 ? 'booking__step--active' : ''} ${animating && step === 3 ? (direction === 'forward' ? 'booking__step--exit-back' : 'booking__step--exit-forward') : ''}`}>
              <div className="booking__step-head">
                <h3 className="booking__step-title display">Your <em>details</em></h3>
                <p className="booking__step-sub">Almost there — just a few things.</p>
              </div>

              <form
                ref={formRef}
                className="booking__form"
                onSubmit={handleSubmit}
                noValidate
              >
                <div className="booking__grid">
                  <Field label="First name" required error={errors.firstName}>
                    <input type="text" name="firstName" placeholder="Your first name"
                      autoComplete="given-name" value={form.firstName}
                      onChange={handleChange} maxLength={60} />
                  </Field>

                  <Field label="Last name" required error={errors.lastName}>
                    <input type="text" name="lastName" placeholder="Your last name"
                      autoComplete="family-name" value={form.lastName}
                      onChange={handleChange} maxLength={60} />
                  </Field>

                  <Field label="Phone" required error={errors.phone}>
                    <input type="tel" name="phone" placeholder="(555) 123-4567"
                      autoComplete="tel" inputMode="tel" value={form.phone}
                      onChange={handleChange} maxLength={30} />
                  </Field>

                  <Field label="Email" required error={errors.email}>
                    <input type="email" name="email" placeholder="you@somewhere.com"
                      autoComplete="email" value={form.email}
                      onChange={handleChange} maxLength={120} />
                  </Field>

                  <Field label="Confirm email" required error={errors.email2} full>
                    <input type="email" name="email2" placeholder="re-type your email"
                      autoComplete="email" value={form.email2}
                      onChange={handleChange} maxLength={120} />
                  </Field>

                  <Field label="Notes (optional)" full>
                    <textarea name="note" rows={3}
                      placeholder="Anything we should know — allergies, references, requests…"
                      value={form.note} onChange={handleChange} maxLength={1000} />
                  </Field>

                  <Field full error={errors.consent}>
                    <label className="booking__consent">
                      <input type="checkbox" name="consent"
                        checked={form.consent} onChange={handleChange} />
                      <span>I agree to receive appointment-related emails from Bella 7.</span>
                    </label>
                  </Field>
                </div>

                {errors._form && (
                  <div className="booking__form-error" role="alert">{errors._form}</div>
                )}

                <div className="booking__step-actions booking__step-actions--split">
                  <button type="button" className="booking__btn-back" onClick={goBack}>
                    ← Back
                  </button>
                  <div className="booking__submit-wrap">
                    <span className="booking__note mono">No deposit · cancel free up to 24h</span>
                    <button type="submit" className="booking__btn-next" disabled={submitting}>
                      <span>{submitting ? 'Reserving…' : 'Reserve my chair'}</span>
                      <span className="booking__arrow" aria-hidden="true">→</span>
                    </button>
                  </div>
                </div>
              </form>
            </div>

          </div>{/* /.booking__steps */}

          <div className="booking__foot">
            <div className="booking__lockup display">Bella <em>7</em></div>
            <div className="mono">© {new Date().getFullYear()} — made with care</div>
          </div>
        </div>

      </div>
    </div>
  );
}

function Field({ label, required, error, children, full }) {
  return (
    <div className={`booking__field${full ? ' booking__field--full' : ''}${error ? ' is-invalid' : ''}`}>
      {label && (
        <label className="booking__label mono">
          {label}{required && <span className="booking__req">*</span>}
        </label>
      )}
      {children}
      {error && <span className="booking__err">{error}</span>}
    </div>
  );
}